import { useEffect, useRef } from 'react';
import { createOrgPath, homePath } from 'src/routes/constants';
import { extractDomainFromEmail } from 'src/utils/domainUtils';
import {useUserQuery} from 'src/views/SignUpPage/queries/useUserQuery';

import { useNavigate } from 'components/Nav';

import { useAutoJoinMutation } from '../views/SignUpPage/queries/useAutoJoinMutation';
import { useCheckClaimedDomainQuery } from '../views/SignUpPage/queries/useCheckClaimedDomainQuery';

import { useGetMyInfoQuery } from './useGetMyInfoQuery';
interface UseAutoJoinReturn {
  isAutoJoinLoading: boolean;
  claimedDomain: any;
  isAutoJoinPending: boolean;
}

/**
 * Custom hook that handles complete auto-join flow for domain claiming
 * Manages domain checking, auto-join attempts, and navigation
 */
export const useAutoJoin = (): UseAutoJoinReturn => {
  const navigate = useNavigate();
  const autoJoinAttemptedRef = useRef(false);
  // Fetch data directly instead of receiving as parameters
  const { data: user } = useUserQuery();
  const { data: myInfo } = useGetMyInfoQuery();
  const { mutate: autoJoin, isPending: isAutoJoinPending } = useAutoJoinMutation();

  const orgId = myInfo?.orgRole?.org.id;
  const invitedEmail = user?.publicMetadata?.invitedEmail as string || undefined;

  // Extract domain from user's email for auto-join check
  const userDomain = extractDomainFromEmail(user?.primaryEmailAddress?.emailAddress);

  // Check if domain is claimed (only when no invite and no existing orgId)
  const shouldEnableQuery = !invitedEmail && !orgId;
  const { data: claimedDomain, isLoading: isLoadingClaimedDomain } = useCheckClaimedDomainQuery(
    userDomain,
    shouldEnableQuery
  );

 // Handle auto-join flow with useEffect - this was the "useAttemptJoin logic"
 useEffect(() => {
  // Only attempt if not already attempted, domain check complete, and domain is claimed
  if (!autoJoinAttemptedRef.current &&
    !isLoadingClaimedDomain &&
    !!claimedDomain?.parentId &&
    !!myInfo?.builder?.id &&
    !invitedEmail&&
    !orgId) {

    console.debug('Domain is claimed, attempting auto-join', claimedDomain);
    autoJoinAttemptedRef.current = true;

    autoJoin({
      data: {
        orgId: claimedDomain.parentId,
        builderId: myInfo.builder.id,
      },
      toastId: 'auto-join',
    }, {
      onSuccess: () => {
        console.debug('Auto-join successful, navigating to /home/projects');
        navigate(homePath);
      },
      onError: (err) => {
        console.error('Auto-join failed', err);
        // Fall back to org creation
        navigate(createOrgPath);
      },
    });
  }
}, [
  isLoadingClaimedDomain,
  isAutoJoinPending,
  claimedDomain,
  myInfo,
  invitedEmail,
  orgId,
  autoJoin,
  navigate,
]);

  return {
    isAutoJoinLoading: isLoadingClaimedDomain,
    claimedDomain,
    isAutoJoinPending,
  };
}